import { useTRPC } from '@/utils/trpc';
import { getTRPCErrorCode } from '@/utils/trpcErrorCode';
import { USER_UPLOAD_DOCUMENT_CATEGORY_SEARCH_TOKEN } from '@liza/fhir-utils';
import type { PatientAdminApiRouterOutputs } from '@liza/patient-admin-api';
import { QueryStateHandler } from '@liza/ui/components/QueryStateHandler';
import { DocumentsList } from '@liza/ui/components/records';
import {
  type RecordsListFilters,
  defaultFilters,
  RecordsListControls,
} from '@liza/ui/components/records/RecordsListControls';
import { Spinner } from '@liza/ui/components/ui/spinner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useDebouncedCallback } from 'use-debounce';
import type { DetailPanelView } from '../types';

type Patient = PatientAdminApiRouterOutputs['patient']['get']['patient'];

type Props = {
  onViewChange: (view: DetailPanelView | null) => void;
  patient: Patient;
  selectedId?: string;
};

/** Client-side mirror of the server's `USER_UPLOAD_CONTENT_TYPES` allowlist. */
const ACCEPTED_UPLOAD_CONTENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'text/plain',
] as const;

type AcceptedUploadContentType = (typeof ACCEPTED_UPLOAD_CONTENT_TYPES)[number];

/** Client-side mirror of the server's `MAX_USER_UPLOAD_BYTES` cap (`@liza/services` is backend-only). */
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

export const Documents = ({ onViewChange, patient, selectedId }: Props): React.ReactElement => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [filters, setFilters] = useState<RecordsListFilters>(defaultFilters);
  const handleFiltersChange = useDebouncedCallback(setFilters, 300);
  const [uploadError, setUploadError] = useState<string>();

  const listOptions = {
    ownership: filters.ownership,
    searchParams: { category: USER_UPLOAD_DOCUMENT_CATEGORY_SEARCH_TOKEN },
    status: filters.status,
  };

  const documentsQuery = useQuery(
    trpc.patient.documentReference.list.queryOptions({ medplumPatientId: patient.medplumId, options: listOptions }),
  );

  // One pending flag spans all three upload steps (createUploadUrl → PUT → finalizeUpload),
  // so the mutations' own isPending states aren't used.
  const [isUploading, setIsUploading] = useState(false);

  const createUploadUrlMutation = useMutation(trpc.patient.documentReference.createUploadUrl.mutationOptions());
  const finalizeUploadMutation = useMutation(
    trpc.patient.documentReference.finalizeUpload.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: trpc.patient.documentReference.list.queryKey({ medplumPatientId: patient.medplumId }),
        });
      },
    }),
  );
  const { mutateAsync: createUploadUrl } = createUploadUrlMutation;
  const { mutateAsync: finalizeUpload } = finalizeUploadMutation;

  /**
   * Two-step upload: mint a presigned write URL, PUT the raw file straight to storage (no
   * base64, no API Gateway/Lambda in the byte path), then finalize so the server verifies the
   * stored bytes and wraps them in a DocumentReference. Failures map by tRPC error code, not
   * by step — PAYLOAD_TOO_LARGE and BAD_REQUEST (in practice only thrown at finalize) show
   * the same inline messages as the client-side gates; everything else gets the generic toast.
   */
  const uploadFile = async (file: File, contentType: AcceptedUploadContentType): Promise<void> => {
    const { medplumId: medplumPatientId } = patient;
    setIsUploading(true);
    try {
      const { binaryId, uploadUrl } = await createUploadUrl({ medplumPatientId, filename: file.name, contentType });
      const putResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': contentType },
      });
      if (!putResponse.ok) {
        throw new Error(`Presigned upload failed with status ${putResponse.status}`);
      }
      await finalizeUpload({ medplumPatientId, binaryId, filename: file.name, contentType });
    } catch (error) {
      const code = getTRPCErrorCode(error);
      if (code === 'PAYLOAD_TOO_LARGE') {
        setUploadError(t('documentUploadTooLarge'));
      } else if (code === 'BAD_REQUEST') {
        setUploadError(t('documentUploadInvalidType'));
      } else {
        toast.error(t('documentUploadFailed'));
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadClick = (): void => {
    if (isUploading) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    // Reset so picking the same file again re-fires the change event.
    e.target.value = '';
    if (!file) return;

    if (!ACCEPTED_UPLOAD_CONTENT_TYPES.includes(file.type as AcceptedUploadContentType)) {
      setUploadError(t('documentUploadInvalidType'));
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      setUploadError(t('documentUploadTooLarge'));
      return;
    }
    setUploadError(undefined);

    void uploadFile(file, file.type as AcceptedUploadContentType);
  };

  const searchQuery = filters.searchQuery.trim().toLowerCase();

  return (
    <div className="flex flex-col gap-2 p-2">
      <RecordsListControls
        title={t('documents')}
        searchPlaceholder={t('searchDocuments')}
        onFiltersChange={handleFiltersChange}
        onAdd={handleUploadClick}
        addLabel={t('uploadDocument')}
        addTestId="upload-document-button"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_UPLOAD_CONTENT_TYPES.join(',')}
        className="hidden"
        onChange={handleFileChange}
        aria-label={t('uploadDocument')}
        data-testid="document-upload-input"
      />
      {uploadError && (
        <p role="alert" className="px-3 text-sm text-destructive" data-testid="document-upload-error">
          {uploadError}
        </p>
      )}
      {isUploading && (
        <div
          className="flex items-center gap-2 px-3 text-sm text-muted-foreground"
          data-testid="document-upload-pending"
        >
          <Spinner className="size-4" />
          {t('uploadingDocument')}
        </div>
      )}
      <QueryStateHandler query={documentsQuery} emptyMessage={t('noDocumentsFound')}>
        {(data) => {
          // Uploads aren't server-searchable by filename, so the search box filters client-side.
          const documents = searchQuery
            ? data.documentReferences.filter((doc) =>
                (doc.content?.[0]?.attachment?.title ?? '').toLowerCase().includes(searchQuery),
              )
            : data.documentReferences;
          return (
            <DocumentsList
              documents={documents}
              onSelectDocument={(id) => onViewChange({ type: 'document-detail', id })}
              selectedDocumentId={selectedId}
            />
          );
        }}
      </QueryStateHandler>
    </div>
  );
};
